// src/rtkQuery/features/services/userNotification/userNotificationApi.slice.ts

import { baseApi } from '@/rtkQuery/features/services/baseApi';
import { buildQuery, handleAuthError } from '@/rtkQuery/lib';
import { NOTIFICATION_ENDPOINTS } from '@/rtkQuery/config/notificationApi.config';
import type {
  NotificationResponse 
} from '@/rtkQuery/features/models/notification.model';

export const notificationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getNotifications: builder.query<NotificationResponse, void>({
      query: buildQuery<void>(NOTIFICATION_ENDPOINTS.GET_ALL, 'GET'),
      transformResponse: (response: NotificationResponse, meta) => {
        (meta as any).status = 'success';
        (meta as any).error = null;
        return response;
      },
      transformErrorResponse: (response: any, meta) => {
        const errorMessage = handleAuthError({
          message: response.data || response.data?.message,
          status: response.status,
        });
        (meta as any).status = 'error';
        (meta as any).error = errorMessage;

        return {
          status: response.status,
          message: errorMessage,
          errors: response.data || response.data?.errors || {},
        };
      },
    }),

    acknowledgeNotification: builder.mutation<any, number>({
      query: (notificationId) => ({
        url: `${NOTIFICATION_ENDPOINTS.ACKNOWLEDGE}?notificationId=${notificationId}`,
        method: 'PATCH',
      }),
      async onQueryStarted(notificationId, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            notificationApi.util.updateQueryData('getNotifications', undefined, (draft) => {
              const notify = draft.find((n) => n.Id === notificationId);
              if (notify) notify.UserAcknowledged = true;
            })
          );
        } catch {
          // Optionally handle error
        }
      },
    }),

    markNotificationAsViewed: builder.mutation<any, number>({
      query: (notificationId) => ({
        url: `${NOTIFICATION_ENDPOINTS.VIEWED}?notificationId=${notificationId}`,
        method: 'PATCH',
      }),
      async onQueryStarted(notificationId, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            notificationApi.util.updateQueryData('getNotifications', undefined, (draft) => {
              const notify = draft.find((n) => n.Id === notificationId);
              if (notify) notify.UserViewed = true;
            })
          );
        } catch {
          // Optionally handle error
        }
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetNotificationsQuery,
  useAcknowledgeNotificationMutation,
  useMarkNotificationAsViewedMutation,
} = notificationApi;